import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid, 
  Alert, 
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Avatar,
  FormGroup,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Switch,
  Radio,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  SwapHoriz as TransferIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import NPIProviderSearch from '../admin/NPIProviderSearch.jsx';

import { InpatientService } from '../../services/inpatientService'; 
import { useUserData } from '../../hooks/useUserData';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const CompactCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  }
}));



const PatientTransfer = () => {
  
  // Use the new useUserData hook for consistent data structure
  const { user, defaultFacility } = useUserData();
  
  // All hooks must be declared at the top level, before any conditional returns
  const [encounters, setEncounters] = useState([]);
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [newLocation, setNewLocation] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [transferHistory, setTransferHistory] = useState([]);
  const [transferType, setTransferType] = useState('internal');
  const [departments, setDepartments] = useState([]);
  const [externalMode, setExternalMode] = useState('npi'); // 'npi' or 'manual'
  const [selectedExternalProvider, setSelectedExternalProvider] = useState(null);
  const [manualExternal, setManualExternal] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    fax: ''
  });
  
    // Fetch active inpatient encounters
  const fetchEncounters = useCallback(async () => {
    if (!defaultFacility?.id) {
      console.log('No default facility available');
      setEncounters([]);
      return;
    }
    
    setLoading(true);
    try {
      console.log('🔄 PatientTransfer: Fetching encounters for facility:', defaultFacility.id);
      
      // Use the same service method as InpatientList
      const encountersData = await InpatientService.getInpatientsByFacility(defaultFacility.id, 100);
      console.log('✅ PatientTransfer: Got', encountersData.length, 'encounters');
      
      // Transform encounters to match expected format
      const encountersWithPatients = encountersData.map(encounter => ({
        ...encounter,
        patient: {
          id: encounter.patientId,
          name: encounter.patientName || 'Unknown Patient',
          mrn: encounter.patientMRN || encounter.mrn || 'N/A'
        }
      }));

      setEncounters(encountersWithPatients);
    } catch (error) {
      console.error('❌ PatientTransfer: Error fetching encounters:', error);
      setError('Failed to fetch encounters');
      setEncounters([]);
    } finally {
      setLoading(false);
    }
  }, [defaultFacility]);

  // Fetch transfer history
  const fetchTransferHistory = useCallback(async () => {
    if (!selectedEncounter?.id) return;
    
    try {
      const response = await InpatientService.getTransferHistory(selectedEncounter.id);
      setTransferHistory(response.transferHistory || []);
    } catch (error) {
      console.error('❌ PatientTransfer: Error fetching transfer history:', error);
      setError('Failed to fetch transfer history');
    }
  }, [selectedEncounter?.id]);

  // Fetch departments for selected facility (for internal transfer)
  useEffect(() => {
    const fetchDepartments = async () => {
      if (transferType === 'internal' && defaultFacility?.id) {
        try {
          console.log('🔄 PatientTransfer: Fetching departments for facility:', defaultFacility.id);
          const response = await InpatientService.getFacilityDepartments(defaultFacility.id);
          setDepartments(response.departments || []);
          console.log('✅ PatientTransfer: Got', response.departments?.length || 0, 'departments');
        } catch (err) {
          console.error('❌ PatientTransfer: Error fetching departments:', err);
          setDepartments([]);
        }
      } else {
        console.log('PatientTransfer: No facility ID available or not internal transfer, clearing departments');
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, [transferType, defaultFacility?.id]);

  useEffect(() => { 
    if (defaultFacility?.id) {
      fetchEncounters(); 
    } else if (user && !defaultFacility) {
      console.log('User loaded but no default facility available');
      setError('No default facility available. Please contact your administrator.');
    }
  }, [fetchEncounters, defaultFacility?.id, user, defaultFacility]);

  useEffect(() => {
    if (selectedEncounter) {
      fetchTransferHistory();
    }
  }, [selectedEncounter, fetchTransferHistory]);



  const handleEncounterSelect = (encounter) => {
    setSelectedEncounter(encounter);
    setNewLocation('');
    setTransferReason('');
    setSuccess('');
    setError('');
  };

  const handleManualExternalChange = (field, value) => {
    setManualExternal(prev => ({ ...prev, [field]: value }));
  };

  const handleNPIProviderSelect = (provider) => {
    setSelectedExternalProvider(provider);
    // Populate manualExternal fields for Firestore and UI
    setManualExternal({
      name: provider?.basic?.organization_name || provider?.basic?.name || '',
      address: provider?.addresses ? (
        (provider.addresses[0]?.address_1 || '') + ', ' +
        (provider.addresses[0]?.city || '') + ', ' +
        (provider.addresses[0]?.state || '') + ' ' +
        (provider.addresses[0]?.postal_code || '')
      ) : '',
      email: '', // NPI does not provide email
      phone: provider?.addresses ? provider.addresses[0]?.telephone_number || '' : '',
      fax: provider?.addresses ? provider.addresses[0]?.fax_number || '' : ''
    });
  };

  // Handle transfer
  const handleTransfer = async () => {
    if (!selectedEncounter || !newLocation || !transferReason) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const transferData = {
        transferType,
        fromLocation: selectedEncounter.locationId || selectedEncounter.bedUnit || 'Unknown',
        toLocation: newLocation,
        transferReason,
        transferDate: new Date().toISOString(),
        externalProvider: transferType === 'external' ? selectedExternalProvider : null,
        manualExternal: transferType === 'external' && externalMode === 'manual' ? manualExternal : null
      };

      await InpatientService.createTransferRecord(selectedEncounter.id, transferData);

      // Update encounter location
      await InpatientService.updatePatient(selectedEncounter.patient.id, {
        currentLocation: newLocation,
        lastTransferDate: new Date().toISOString()
      });

      setSuccess('Transfer completed successfully');
      setNewLocation('');
      setTransferReason('');
      setSelectedEncounter(null);
      
      // Refresh data
      await fetchEncounters();
      await fetchTransferHistory();
    } catch (error) {
      console.error('Error completing transfer:', error);
      setError('Failed to complete transfer');
    } finally {
      setLoading(false);
    }
  };

  // Check for user and facility access before rendering
  if (!user) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user.facilityId && !defaultFacility?.id && (!user.facilities || user.facilities.length === 0)) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Alert severity="warning">
          No facility access available. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                <TransferIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Patient Transfer
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Transfer patients between hospital units and locations
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      <Grid container spacing={2} alignItems="stretch">
        {/* Patient Selection */}
        <Grid item xs={12} md={4}>
          <CompactCard sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Select Patient
                </Typography>
              </Box>
              
              <FormControl fullWidth size="small">
                <InputLabel>Inpatient Encounter</InputLabel>
                <Select
                  value={selectedEncounter?.id || ''}
                  label="Inpatient Encounter"
                  onChange={(e) => {
                    const encounter = encounters.find(enc => enc.id === e.target.value);
                    handleEncounterSelect(encounter);
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography>Loading encounters...</Typography>
                      </Box>
                    </MenuItem>
                  ) : encounters.length === 0 ? (
                    <MenuItem disabled>
                      <Typography color="text.secondary">No active inpatient encounters found</Typography>
                    </MenuItem>
                  ) : (
                    encounters.map(enc => (
                      <MenuItem key={enc.id} value={enc.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {enc.patient?.name || 'Unknown Name'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {enc.locationId || enc.bedUnit || 'No location'} • {enc.reason || enc.diagnosis || 'No reason'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {selectedEncounter && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Current Patient Info
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                        {selectedEncounter.patient?.name?.charAt(0) || 'P'}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedEncounter.patient?.name || selectedEncounter.patientId}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Current: {selectedEncounter.locationId || 'Not assigned'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Admitted: {selectedEncounter.startDate?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </CompactCard>
        </Grid>

        {/* Transfer Form */}
        <Grid item xs={12} md={5}>
          <CompactCard sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TransferIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Transfer Details
                </Typography>
              </Box>

              <form onSubmit={(e) => { e.preventDefault(); handleTransfer(); }}>
                <Stack spacing={1.5}>
                  {/* Transfer Type Selector */}
                  <FormControl component="fieldset" size="small">
                    <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>Transfer Type</FormLabel>
                    <RadioGroup
                      row
                      value={transferType}
                      onChange={e => {
                        setTransferType(e.target.value);
                        setNewLocation('');
                      }}
                    >
                      <FormControlLabel 
                        value="internal" 
                        control={<Radio size="small" />} 
                        label={<Typography variant="body2">Internal (Department)</Typography>} 
                      />
                      <FormControlLabel 
                        value="external" 
                        control={<Radio size="small" />} 
                        label={<Typography variant="body2">External (Facility)</Typography>} 
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Internal Transfer: Department Dropdown */}
                  {transferType === 'internal' && (
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={newLocation}
                        label="Department"
                        onChange={e => setNewLocation(e.target.value)}
                      >
                        {departments.map(dept => (
                          <MenuItem key={dept.id || dept.name} value={dept.name}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* External Transfer: NPI or Manual Mode */}
                  {transferType === 'external' && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>External Transfer Mode</Typography>
                        <FormControlLabel
                          control={<Switch size="small" checked={externalMode === 'manual'} onChange={e => setExternalMode(e.target.checked ? 'manual' : 'npi')} />}
                          label={<Typography variant="body2">{externalMode === 'manual' ? 'Manual Entry' : 'Search by NPI'}</Typography>}
                          sx={{ ml: 0 }}
                        />
                      </Box>
                      {externalMode === 'npi' ? (
                        <NPIProviderSearch onProviderSelect={handleNPIProviderSelect} />
                      ) : (
                        <Stack spacing={1}>
                          <TextField
                            label="Facility/Provider Name"
                            fullWidth
                            size="small"
                            value={manualExternal.name}
                            onChange={e => handleManualExternalChange('name', e.target.value)}
                            required
                          />
                          <TextField
                            label="Address"
                            fullWidth
                            size="small"
                            value={manualExternal.address}
                            onChange={e => handleManualExternalChange('address', e.target.value)}
                            required
                          />
                          <TextField
                            label="Email"
                            fullWidth
                            size="small"
                            value={manualExternal.email}
                            onChange={e => handleManualExternalChange('email', e.target.value)}
                          />
                          <TextField
                            label="Phone"
                            fullWidth
                            size="small"
                            value={manualExternal.phone}
                            onChange={e => handleManualExternalChange('phone', e.target.value)}
                          />
                          <TextField
                            label="Fax"
                            fullWidth
                            size="small"
                            value={manualExternal.fax}
                            onChange={e => handleManualExternalChange('fax', e.target.value)}
                          />
                        </Stack>
                      )}
                    </>
                  )}

                  <TextField
                    label="Transfer Reason"
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    placeholder="Reason for transfer (optional)"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={
                      loading ||
                      !selectedEncounter ||
                      (transferType === 'internal' && !newLocation) ||
                      (transferType === 'external' && externalMode === 'npi' && !selectedExternalProvider) ||
                      (transferType === 'external' && externalMode === 'manual' && (!manualExternal.name.trim() || !manualExternal.address.trim()))
                    }
                    startIcon={loading ? <CircularProgress size={14} /> : <TransferIcon sx={{ fontSize: 18 }} />}
                    sx={{ 
                      borderRadius: 1.5, 
                      textTransform: 'none', 
                      fontWeight: 600,
                      py: 0.75
                    }}
                  >
                    {loading ? 'Transferring...' : 'Transfer Patient'}
                  </Button>

                  {error && (
                    <Alert severity="error" sx={{ py: 0 }}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ py: 0 }}>
                      {success}
                    </Alert>
                  )}
                </Stack>
              </form>
            </CardContent>
          </CompactCard>
        </Grid>

        {/* Transfer History */}
        <Grid item xs={12} md={3}>
          <CompactCard sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Transfer History
                </Typography>
              </Box>

              {selectedEncounter ? (
                <List dense sx={{ p: 0 }}>
                  {transferHistory.length > 0 ? (
                    transferHistory.map((transfer, index) => (
                      <ListItem key={transfer.id} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {transfer.toLocation}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {transfer.transferredAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                      No transfer history
                    </Typography>
                  )}
                </List>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  Select a patient to view transfer history
                </Typography>
              )}
            </CardContent>
          </CompactCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientTransfer; 